"use client"

import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import {
  Box,
  Chip,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material"
import { Block, CheckCircleOutline, Delete, LockReset } from "@mui/icons-material"
import { useNotification } from "../../contexts/NotificationContext"
import { useAuth } from "../../contexts/AuthContext"
import PageHeader from "../../components/common/PageHeader"
import ConfirmDialog from "../../components/common/ConfirmDialog"
import UserForm from "../../components/users/UserForm"
import ResetPasswordDialog from "../../components/users/ResetPasswordDialog"
import { getUser, updateUser, deleteUser } from "../../services/user.service"
import { getActiveOrganizations } from "../../services/organization.service"

const UserDetails = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { showNotification } = useNotification()
  const { user: currentUser } = useAuth()
  const [user, setUser] = useState(null)
  const [organizations, setOrganizations] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [resetDialogOpen, setResetDialogOpen] = useState(false)
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false)

  // Add this check to redirect if currentUser is null
  useEffect(() => {
    if (!currentUser) {
      navigate("/login")
      return
    }
  }, [currentUser, navigate])

  const isSuperAdmin = currentUser?.role === "superadmin"
  const isProtectedTarget = user?.role === "superadmin" && !isSuperAdmin
  const canManageUser =
    ["admin", "superadmin"].includes(currentUser?.role) && id !== currentUser?._id && !isProtectedTarget

  useEffect(() => {
    fetchUser()
    if (isSuperAdmin) {
      fetchOrganizations()
    }
  }, [id, isSuperAdmin])

  const fetchUser = async () => {
    setLoading(true)
    try {
      const response = await getUser(id)
      setUser(response.data)
    } catch (error) {
      showNotification("Failed to fetch user details", "error")
      navigate("/users")
    } finally {
      setLoading(false)
    }
  }

  const fetchOrganizations = async () => {
    try {
      const response = await getActiveOrganizations()
      setOrganizations(response.data)
    } catch (error) {
      showNotification("Failed to fetch organizations", "error")
    }
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
  }

  const handleUpdate = async (formData) => {
    setSaving(true)
    try {
      const response = await updateUser(id, formData)
      setUser(response.data)
      showNotification("User updated successfully", "success")
    } catch (error) {
      showNotification(error.message || "Failed to update user", "error")
    } finally {
      setSaving(false)
    }
  }

  const handleSetActive = async (isActive) => {
    try {
      const response = await updateUser(id, { isActive })
      setUser(response.data)
      showNotification(isActive ? "User activated" : "User deactivated", "success")
    } catch (error) {
      showNotification(error.message || "Failed to update user status", "error")
    } finally {
      setDeactivateDialogOpen(false)
    }
  }

  const handleDelete = async () => {
    try {
      await deleteUser(id)
      showNotification("User deleted successfully", "success")
      navigate("/users")
    } catch (error) {
      showNotification(error.message || "Failed to delete user", "error")
    } finally {
      setDeleteDialogOpen(false)
    }
  }

  if (loading || !user) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <PageHeader
        title={`${user.firstName} ${user.lastName}`}
        subtitle={user.email}
        breadcrumbs={[
          { text: "Dashboard", link: "/dashboard" },
          { text: "Users", link: "/users" },
          { text: `${user.firstName} ${user.lastName}` },
        ]}
        action={id !== currentUser?._id && !isProtectedTarget}
        actionText="Delete User"
        actionIcon={<Delete />}
        actionColor="error"
        onActionClick={() => setDeleteDialogOpen(true)}
      />

      {canManageUser && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1, mb: 2 }}>
          {user.isActive === false && <Chip label="Inactive" color="warning" size="small" sx={{ mr: "auto" }} />}
          {user.isActive === false ? (
            <Button variant="outlined" startIcon={<CheckCircleOutline />} onClick={() => handleSetActive(true)}>
              Activate
            </Button>
          ) : (
            <Button variant="outlined" color="warning" startIcon={<Block />} onClick={() => setDeactivateDialogOpen(true)}>
              Deactivate
            </Button>
          )}
          <Button variant="outlined" startIcon={<LockReset />} onClick={() => setResetDialogOpen(true)}>
            Reset Password
          </Button>
        </Box>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="user tabs"
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="Details" />
          {/* Add more tabs as needed */}
        </Tabs>

        <Box sx={{ p: 3 }}>
          {activeTab === 0 && (
            <UserForm
              user={user}
              onSubmit={handleUpdate}
              loading={saving}
              isEdit={true}
              organizations={organizations}
            />
          )}
        </Box>
      </Paper>

      <ResetPasswordDialog open={resetDialogOpen} user={user} onClose={() => setResetDialogOpen(false)} />

      <ConfirmDialog
        open={deactivateDialogOpen}
        title="Deactivate user"
        message={`${user.firstName} ${user.lastName} will no longer be able to sign in or use the app until you activate them again.`}
        confirmText="Deactivate"
        confirmColor="warning"
        onConfirm={() => handleSetActive(false)}
        onCancel={() => setDeactivateDialogOpen(false)}
      />

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {user.firstName} {user.lastName}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default UserDetails

